import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { warrantyPairToken, withoutPairMarkers } from '@theme/warranty-pair';

/**
 * Warranty upsell after an add-to-cart without a selected warranty tier (PP-863 use
 * case 5, extended for product lists in PP-906 and for the add-to-cart popup in
 * PP-987).
 *
 * Two presentations, picked by the theme setting "Enable upsell popup instead of
 * cart drawer":
 *
 * 1. Upsell drawer (setting off). product-form.js opens the drawer after the add
 *    (the cart drawer stays closed for that add). Tiles behave like checkboxes; the
 *    continue button links the picked tier to the just-added parent line via a
 *    shared unique _warranty_pair_id token (the cart transform function computes the
 *    price from that link). Whatever way the drawer is answered, the cart drawer
 *    opens afterwards. Dismissing without picking anything opts the product out for
 *    the session.
 *
 *    Two ways a drawer gets onto the page:
 *    - PDP: rendered by blocks/warranty-addons.liquid inside the buy-box block.
 *    - Product lists (PP-906): no drawer is rendered with the cards. On a card
 *      add-to-cart, product-form.js requests sections/warranty-upsell.liquid for the
 *      added product (Section Rendering API), injects the markup into the body and
 *      fires 'warranty:drawer-injected' so this module binds it (see the listener at
 *      the bottom). Injected drawers stay in the DOM, which keeps the per-page-view
 *      opt-out working for repeated adds. Entry point: #shouldOpenWarrantyUpsell /
 *      #resolveWarrantyDrawer in product-form.js.
 *
 * 2. Options inside the add-to-cart popup (setting on, PP-987). No drawer at all:
 *    product-form.js provides the options of the added product (on the product
 *    page a copy of the markup inside blocks/warranty-addons.liquid, in product
 *    lists sections/warranty-upsell-inline.liquid), atc-upsell-popup.js places the
 *    markup inside the popup and binds it with bindWarrantyPopupOptions() below. A
 *    tier is added to or removed from the cart right when it is toggled, because
 *    the popup is left through plain links (cart, checkout) and has no confirm step.
 *    The wishlist page opens the same popup for the adds of the wishlist app
 *    (se-wishlist-page-override.js).
 */

/**
 * Opens the cart drawer. A header can render its actions twice, one copy per
 * breakpoint, with the unused copy hidden by CSS (PP-987). The drawer inside the
 * hidden copy must never be opened: a modal dialog inside a hidden element blocks
 * the whole page without showing anything. A header with a single drawer keeps
 * using that drawer in any case.
 */
export function openCartDrawer() {
  customElements.whenDefined('cart-drawer-component').then(() => {
    const drawers = Array.from(document.querySelectorAll('cart-drawer-component'));
    const cartDrawer = /** @type {any} */ (
      drawers.find((drawer) => drawer.getClientRects().length > 0) ??
        (drawers.length === 1 ? drawers[0] : null)
    );
    if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
  });
}

/**
 * Section ids of every rendered cart surface (cart page, cart drawer — the
 * drawer lives in the header section). Mirrors component-cart-items.js.
 * @returns {string[]}
 */
function collectCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  });
  const headerSection = document.querySelector('cart-icon')?.closest('.shopify-section');
  if (headerSection?.id) {
    ids.add(headerSection.id.replace('shopify-section-', ''));
  }
  return Array.from(ids);
}

/** @returns {Record<string, string>} */
function cartSectionParams() {
  const sectionIds = collectCartSectionIds();
  return sectionIds.length
    ? { sections: sectionIds.join(','), sections_url: window.location.pathname }
    : {};
}

/**
 * Item count inside rendered cart markup (hidden element of cart-products.liquid).
 * Null when the markup carries none (empty cart).
 * @param {string | undefined} html
 * @returns {number | null}
 */
function itemCountFromSection(html) {
  if (!html) return null;
  const text = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('[ref="cartItemCount"]')?.textContent;
  const count = Number.parseInt(text ?? '', 10);
  return Number.isNaN(count) ? null : count;
}

// Newest run of refreshCartSurfaces.
let cartSurfacesRun = 0;

/**
 * Renders every cart surface freshly and announces the change (PP-987). The
 * popup flow confirms a tier toggle as soon as the cart holds it; the markup of
 * the header section with the cart drawer is heavy and follows afterwards. Every
 * request gets its own URL so a render of the same section that is still pending
 * from the page load never answers it with older markup.
 *
 * The event carries the cart itself: every product form on the page reads its
 * cart quantity from it and would otherwise request the cart on its own (one
 * request per product card of a list).
 *
 * Runs can overlap: the wishlist page refreshes after its add
 * (se-wishlist-page-override.js) while a tier toggle of the popup can follow
 * right away. Only the newest run announces its markup, so an answer of an older
 * run that arrives late never puts older markup back.
 * @param {number} [fallbackItemCount] - used when no markup carries a count and the cart cannot be read
 */
export async function refreshCartSurfaces(fallbackItemCount) {
  const run = ++cartSurfacesRun;
  /** @type {Record<string, string>} */
  const sections = {};
  /** @type {any} */
  let cart = null;
  await Promise.all([
    ...collectCartSectionIds().map(async (sectionId) => {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('cart_sync', `${Date.now()}-${sectionId}`);
      try {
        sections[sectionId] = await sectionRenderer.getSectionHTML(sectionId, false, url);
      } catch (error) {
        console.error(`Cart section ${sectionId} could not be rendered:`, error);
      }
    }),
    readCart().then((data) => {
      cart = data;
    }),
  ]);
  if (run !== cartSurfacesRun) return;
  const itemCount =
    Object.values(sections)
      .map(itemCountFromSection)
      .find((count) => count !== null) ??
    cart?.item_count ??
    fallbackItemCount;
  // Nothing could be read and the caller knows no count either: the cart surfaces
  // keep what they show.
  if (itemCount === undefined) return;
  document.dispatchEvent(
    new CartUpdateEvent(cart ?? {}, 'warranty-addons', {
      source: 'warranty-addons',
      itemCount,
      sections,
    })
  );
}

/**
 * The cart, or null when it cannot be read.
 * @returns {Promise<any>}
 */
async function readCart() {
  try {
    const response = await fetch('/cart.js');
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<any>} the parsed response, throws on a failed request
 */
async function postCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} failed with status ${response.status}`);
  return response.json();
}

/** @param {any} item */
const isWarrantyLine = (item) => item?.properties?._warranty === 'true';

/**
 * PP-1019: warranties are for business customers only. The cart attribute decides;
 * a private cart also brings the page in line (tiles hidden, switch updated).
 * @param {any} cart
 * @returns {boolean}
 */
function cartIsPrivateCustomer(cart) {
  const type = String(cart?.attributes?.customer_type ?? '').toLowerCase();
  if (type !== 'b2c') return false;
  const Selector = /** @type {any} */ (customElements.get('client-type-selector'));
  Selector?.applyCartType?.('b2c');
  return true;
}

/**
 * Pair reference of a cart line, with the fallback to the legacy property of
 * older sessions.
 * @param {any} item
 * @returns {string | null}
 */
const pairRefOf = (item) =>
  item?.properties?._warranty_pair_id ?? item?.properties?._warranty_ref ?? null;

/**
 * Properties of a product line without the warranty markers. The cart ignores a
 * properties update with an empty object, so a line without any other property
 * keeps a blank hidden token instead: that still replaces the whole properties
 * object and thereby drops the visible SKU marker.
 * @param {Record<string, string> | null | undefined} properties
 * @returns {Record<string, string>}
 */
function withoutWarrantyMarkers(properties) {
  const cleaned = { ...properties };
  delete cleaned.warrantySKU;
  delete cleaned._warranty_pair_id;
  delete cleaned._warranty_ref;
  return Object.keys(cleaned).length ? cleaned : { _warranty_pair_id: '' };
}

/**
 * Runs a repair step, a second time after a short pause when it fails: the cart
 * is in an in-between state at that point and a passing network error must not
 * leave it there. The steps read the cart first and only change what is still
 * off, so a second run never applies a change twice.
 * @param {() => Promise<any>} task
 */
async function withRetry(task) {
  try {
    return await task();
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return task();
  }
}

/**
 * Links a warranty to a product line (upsell drawer): the product line gets the
 * visible SKU marker (order export) and the hidden pair token, the warranty line
 * is added with the same token and the quantity of the product line.
 *
 * The warranty line comes first: a tier that cannot be added leaves the cart as
 * it is. The pair token is the same for identical pairs (warranty-pair.js), so
 * the cart merges the product line into a pair of the same product and tier that
 * is already in the cart, and the warranty line into its warranty line.
 * @param {any} parent - product line without a warranty
 * @param {{ warrantyVariantId: string, warrantySku?: string }} options
 * @returns {Promise<{ pairId: string, itemCount: number, sections: Record<string, string> | undefined, cart: any } | null>}
 */
async function attachWarranty(parent, { warrantyVariantId, warrantySku }) {
  const ref = warrantyPairToken(parent.variant_id, warrantyVariantId, parent.properties);

  const properties = { ...parent.properties, warrantySKU: warrantySku, _warranty_pair_id: ref };
  delete properties._warranty_ref;

  await postCart('/cart/add.js', {
    items: [
      {
        id: Number(warrantyVariantId),
        quantity: parent.quantity,
        properties: { _warranty: 'true', _warranty_pair_id: ref },
      },
    ],
  });

  try {
    // change.js replaces the whole properties object of the line.
    const changedCart = await postCart('/cart/change.js', {
      id: parent.key,
      quantity: parent.quantity,
      properties,
      ...cartSectionParams(),
    });
    return {
      pairId: ref,
      sections: changedCart.sections,
      itemCount: changedCart.item_count,
      cart: changedCart,
    };
  } catch (error) {
    // The warranty units that were added have no product units to cover.
    await repairWarrantyPairs();
    console.error('Product line could not be linked to the warranty:', error);
    return null;
  }
}

/**
 * Properties compared as plain key/value maps.
 * @param {Record<string, string>} a
 * @param {Record<string, string>} b
 */
function sameProperties(a, b) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  return keysA.length === keysB.length && keysA.every((key) => String(a[key]) === String(b[key]));
}

/**
 * Product line of an add-to-cart without a warranty: the line of the variant
 * without pair token whose other properties match the add, else the first such line.
 * @param {any} cart
 * @param {string} variantId
 * @param {Record<string, string> | undefined} addedProperties
 */
function findPlainLine(cart, variantId, addedProperties) {
  const plainLines = cart.items.filter(
    (/** @type {any} */ item) =>
      String(item.variant_id) === String(variantId) && !isWarrantyLine(item) && !pairRefOf(item)
  );
  if (addedProperties) {
    const match = plainLines.find((/** @type {any} */ item) =>
      sameProperties(withoutPairMarkers(item.properties), addedProperties)
    );
    if (match) return match;
  }
  return plainLines[0] ?? null;
}

/**
 * Moves the units of one add-to-cart between "no warranty" and a warranty tier
 * (PP-987 popup). Identical pairs share one line (see warranty-pair.js), so the
 * units are cut off a line or merged into one on the way:
 * - the whole source line changes state through its properties (the cart merges
 *   it into a line that already has those properties), the warranty line follows;
 * - part of a line leaves it by quantity, together with its share of the warranty
 *   line, and comes back as a line of its own.
 * Requests run one after another: two cart requests at the same time can lose a
 * change (measured). When a request fails in the middle, units that already
 * left their line are put back before the error is passed on, and the caller
 * brings the pairs back in line (repairWarrantyPairs).
 * @param {any} cart
 * @param {object} move
 * @param {string} move.parentVariantId
 * @param {Record<string, string> | undefined} move.addedProperties - properties of the add (finds the plain line)
 * @param {number} move.quantity - units of the add
 * @param {string | null} move.fromPairId - pair token the units carry right now
 * @param {{ variantId: string, sku: string } | null} move.warranty - tier to move to, null for none
 * @param {(pairId: string | null) => void} [move.onMoved] - called once the units carry their new state (a later step may still fail)
 * @returns {Promise<{ pairId: string | null, itemCount: number }>}
 */
async function moveWarrantyUnits(
  cart,
  { parentVariantId, addedProperties, quantity, fromPairId, warranty, onMoved }
) {
  const source = fromPairId
    ? cart.items.find(
        (/** @type {any} */ item) => !isWarrantyLine(item) && pairRefOf(item) === fromPairId
      )
    : findPlainLine(cart, parentVariantId, addedProperties);
  if (!source) throw new Error('Product line not found in the cart');
  const sourceWarranty = fromPairId
    ? (cart.items.find(
        (/** @type {any} */ item) => isWarrantyLine(item) && pairRefOf(item) === fromPairId
      ) ?? null)
    : null;

  const baseProperties = withoutPairMarkers(source.properties);
  const units = Math.min(Math.max(Number(quantity) || 1, 1), source.quantity);
  const toPairId = warranty
    ? warrantyPairToken(parentVariantId, warranty.variantId, baseProperties)
    : null;
  if (toPairId === fromPairId) return { pairId: fromPairId, itemCount: cart.item_count };

  const targetProperties = warranty
    ? { ...baseProperties, warrantySKU: warranty.sku, _warranty_pair_id: toPairId }
    : withoutWarrantyMarkers(source.properties);
  const warrantyItem = warranty
    ? {
        id: Number(warranty.variantId),
        quantity: units,
        properties: { _warranty: 'true', _warranty_pair_id: toPairId },
      }
    : null;

  if (units < source.quantity) {
    // The units leave the line first: adding them first would count them twice
    // for a moment, which the cart rejects for a product with limited stock.
    /** @type {Record<string, number>} */
    const updates = { [source.key]: source.quantity - units };
    if (sourceWarranty) updates[sourceWarranty.key] = Math.max(sourceWarranty.quantity - units, 0);
    await postCart('/cart/update.js', { updates });
    try {
      // One request for the units and their warranty: it is applied completely
      // or not at all (measured).
      await postCart('/cart/add.js', {
        items: [
          { id: Number(parentVariantId), quantity: units, properties: targetProperties },
          ...(warrantyItem ? [warrantyItem] : []),
        ],
      });
    } catch (error) {
      await restoreUnits([source, sourceWarranty]);
      throw error;
    }
  } else {
    // The warranty line comes first: a tier that cannot be added leaves the cart
    // as it is, including a warranty the units already have. When a later step
    // fails, the caller brings the pairs back in line (repairWarrantyPairs).
    if (warrantyItem) await postCart('/cart/add.js', { items: [warrantyItem] });
    await postCart('/cart/change.js', {
      id: source.key,
      quantity: source.quantity,
      properties: targetProperties,
    });
    onMoved?.(toPairId);
    if (sourceWarranty) await postCart('/cart/update.js', { updates: { [sourceWarranty.key]: 0 } });
  }

  return {
    pairId: toPairId,
    itemCount: cart.item_count + (warranty ? units : 0) - (sourceWarranty ? units : 0),
  };
}

/**
 * Puts units back that a move took off their lines without getting them onto
 * the new ones (rollback). Every line gets what it is short of compared to
 * before the move; added with the properties the line had, the units join it
 * again, or bring it back when it left the cart with its last unit.
 * @param {any[]} lines - cart lines as they were before the move (null entries are skipped)
 */
async function restoreUnits(lines) {
  await withRetry(async () => {
    const cart = await (await fetch('/cart.js')).json();
    const items = lines
      .filter(Boolean)
      .map((line) => ({
        id: line.variant_id,
        quantity:
          line.quantity -
          (cart.items.find((/** @type {any} */ item) => item.key === line.key)?.quantity ?? 0),
        properties: line.properties ?? {},
      }))
      .filter((item) => item.quantity > 0);
    if (items.length) await postCart('/cart/add.js', { items });
  });
}

/**
 * Best effort after a failed step: warranty lines without their product line are
 * removed, a warranty line follows the quantity of its product line, product
 * lines that claim a warranty without a warranty line lose the markers. The cart
 * never keeps half a pair.
 */
async function repairWarrantyPairs() {
  try {
    await withRetry(alignWarrantyPairs);
  } catch (error) {
    console.error('Warranty pairs could not be repaired:', error);
  }
}

async function alignWarrantyPairs() {
  const cart = await (await fetch('/cart.js')).json();
  const parents = cart.items.filter(
    (/** @type {any} */ item) => !isWarrantyLine(item) && pairRefOf(item)
  );
  const warranties = cart.items.filter((/** @type {any} */ item) => isWarrantyLine(item));

  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of warranties) {
    const covered = parents
      .filter((/** @type {any} */ parent) => pairRefOf(parent) === pairRefOf(line))
      .reduce((/** @type {number} */ sum, /** @type {any} */ parent) => sum + parent.quantity, 0);
    if (covered !== line.quantity) updates[line.key] = covered;
  }
  if (Object.keys(updates).length) await postCart('/cart/update.js', { updates });

  for (const parent of parents) {
    if (warranties.some((/** @type {any} */ line) => pairRefOf(line) === pairRefOf(parent))) {
      continue;
    }
    await postCart('/cart/change.js', {
      id: parent.key,
      quantity: parent.quantity,
      properties: withoutWarrantyMarkers(parent.properties),
    });
  }
}

/**
 * Adds the picked warranty for the newest cart line of the drawer's parent product.
 * Returns the data for the cart update event (fresh section markup, item count and
 * the cart) so the cart components morph from the response instead of firing their
 * own full section re-renders (those wipe header runtime state like the B2B/B2C
 * toggle and can apply stale markup when they overlap a later cart change), and
 * the product forms read their cart quantity from it instead of requesting the cart.
 * @param {HTMLElement} drawer
 * @param {HTMLElement} link
 * @returns {Promise<{sections: Record<string, string> | undefined, itemCount: number, cart: any} | null>}
 */
async function addWarrantyForParent(drawer, link) {
  const warrantyVariantId = link.dataset.variantId;
  const warrantySku = link.dataset.variantSku;
  const parentVariantId = drawer.dataset.parentVariantId;
  if (!warrantyVariantId || !parentVariantId) return null;

  const cart = await (await fetch('/cart.js')).json();
  // PP-1019: the drawer was rendered for the customer type of that moment; the
  // cart decides now (the customer may have switched elsewhere since).
  if (cartIsPrivateCustomer(cart)) return null;
  // cart.items[0] is the most recently added line.
  const parent = cart.items.find(
    (/** @type {any} */ item) =>
      String(item.variant_id) === String(parentVariantId) &&
      !isWarrantyLine(item) &&
      !item.properties?._warranty_pair_id
  );
  if (!parent) return null;

  return attachWarranty(parent, { warrantyVariantId, warrantySku });
}

function initWarrantyAddons() {
  document.querySelectorAll('dialog-component.warrantyUpsellDrawer').forEach((drawer) => {
    if (!(drawer instanceof HTMLElement) || drawer.dataset.warrantyUpsellBound) return;
    drawer.dataset.warrantyUpsellBound = 'true';

    const dialog = drawer.querySelector('dialog');
    // Session flag: was a warranty added through this drawer since it was opened?
    let addedViaDrawer = false;
    let confirming = false;

    // Tiles toggle like checkboxes — nothing is added until the continue button.
    drawer.querySelectorAll('.warranty-upsell-link').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const alreadyActive = link.classList.contains('active');
        drawer
          .querySelectorAll('.warranty-upsell-link')
          .forEach((item) => item.classList.remove('active'));
        if (!alreadyActive) link.classList.add('active');
      });
    });

    // Continue: add the picked tier (if any), then hand over to the cart drawer.
    drawer.querySelector('.warranty-upsell-continue')?.addEventListener('click', async () => {
      if (confirming) return;
      confirming = true;
      const picked = /** @type {HTMLElement | null} */ (
        drawer.querySelector('.warranty-upsell-link.active')
      );
      try {
        if (picked) {
          const added = await addWarrantyForParent(drawer, picked);
          if (added) {
            addedViaDrawer = true;
            document.dispatchEvent(
              new CartUpdateEvent(added.cart ?? {}, 'warranty-addons', {
                source: 'warranty-addons',
                itemCount: added.itemCount,
                sections: added.sections,
              })
            );
          }
        } else {
          // Continue without picking = conscious decision, don't ask again right away.
          drawer.dataset.warrantyOptout = 'true';
        }
      } catch (error) {
        console.error('Warranty upsell add failed:', error);
      } finally {
        confirming = false;
        picked?.classList.remove('active');
        if (typeof (/** @type {any} */ (drawer).closeDialog) === 'function') {
          /** @type {any} */ (drawer).closeDialog();
        }
      }
    });

    // Any close (X, ESC, backdrop): opt out only when it was a real dismissal —
    // adding a warranty through the drawer must not suppress it for later adds.
    // The cart drawer always follows so the add-to-cart flow finishes visibly.
    dialog?.addEventListener('close', () => {
      if (!addedViaDrawer && !confirming && !drawer.querySelector('.warranty-upsell-link.active')) {
        drawer.dataset.warrantyOptout = 'true';
      }
      addedViaDrawer = false;
      openCartDrawer();
    });
  });
}

/** Counter for the ids of the tier descriptions in the add-to-cart popup. */
let popupDescriptionCount = 0;

/**
 * Screens that show the tier descriptions in the add-to-cart popup. Same condition as
 * the stylesheet of snippets/warranty-upsell-options.liquid, keep both in sync.
 */
const POPUP_DETAILS_MEDIA = '(min-width: 750px) and (min-height: 500px)';

/**
 * PP-987: warranty tiers inside the add-to-cart popup. Every toggle is applied to
 * the cart right away: picking a tier links it to the units of this add, picking
 * the active tier again removes it, picking another tier replaces it. Units of
 * the same product that were in the cart before keep the state they have (see
 * moveWarrantyUnits).
 *
 * The units are found by the pair token once they carry a warranty, otherwise
 * on the line of the added variant that has no warranty.
 *
 * A toggle is confirmed as soon as the cart holds it. The cart surfaces are
 * rendered afterwards (refreshCartSurfaces), toggles run one after another so a
 * later toggle never renders before an earlier one.
 *
 * @param {HTMLElement} root - the [data-warranty-upsell-options] element
 * @param {object} context
 * @param {string} context.parentVariantId - variant that was added to the cart
 * @param {string | null} [context.pairId] - pair token when the add carried a warranty
 * @param {string | null} [context.warrantyVariantId] - warranty variant of that add
 * @param {number} [context.addedQuantity] - units this add put into the cart
 * @param {Record<string, string>} [context.addedProperties] - line item properties of that add
 */
export function bindWarrantyPopupOptions(
  root,
  { parentVariantId, pairId, warrantyVariantId, addedQuantity, addedProperties }
) {
  if (root.dataset.warrantyOptionsBound) return;
  root.dataset.warrantyOptionsBound = 'true';

  const toggles = /** @type {HTMLButtonElement[]} */ (
    Array.from(root.querySelectorAll('.warranty-upsell-option__toggle'))
  );
  const errorMessage = root.querySelector('.warranty-upsell-options__error');
  let currentPairId = pairId ?? null;
  let busy = false;
  /** @type {Promise<void>} */
  let queue = Promise.resolve();

  /** @param {string | null} activeVariantId */
  const render = (activeVariantId) => {
    toggles.forEach((toggle) => {
      const isActive = !!activeVariantId && toggle.dataset.variantId === String(activeVariantId);
      toggle.setAttribute('aria-pressed', String(isActive));
      toggle.closest('.warranty-upsell-option')?.classList.toggle('is-active', isActive);
    });
  };

  /** @param {boolean} isBusy */
  const setBusy = (isBusy) => {
    busy = isBusy;
    root.classList.toggle('is-busy', isBusy);
    root.setAttribute('aria-busy', String(isBusy));
  };

  render(currentPairId && warrantyVariantId ? String(warrantyVariantId) : null);

  // A description switched on in the theme settings sits next to the button, not
  // inside it. The ids are set here because the same markup can be on the page more
  // than once. Phones hide the description, screen readers then skip it as well (the
  // markup is not on the page yet at this point, so the screen size decides).
  const showsDescriptions = window.matchMedia(POPUP_DETAILS_MEDIA).matches;
  toggles.forEach((toggle) => {
    const description = toggle
      .closest('.warranty-upsell-option')
      ?.querySelector('.warranty-upsell-option__description');
    if (!description || !showsDescriptions) return;
    popupDescriptionCount += 1;
    description.id = `warranty-option-description-${popupDescriptionCount}`;
    toggle.setAttribute('aria-describedby', description.id);
  });

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      if (busy) return;

      const pickedVariantId = toggle.dataset.variantId;
      if (!pickedVariantId) return;
      const previousVariantId =
        toggles.find((item) => item.getAttribute('aria-pressed') === 'true')?.dataset.variantId ??
        null;
      const removes = previousVariantId === pickedVariantId;
      // The card shows its new state right away; only this card signals the
      // running cart requests (see the stylesheet of warranty-upsell-options).
      const option = toggle.closest('.warranty-upsell-option');

      setBusy(true);
      option?.classList.add('is-pending');
      if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;
      render(removes ? null : pickedVariantId);

      queue = queue.then(async () => {
        /** @type {{ itemCount: number } | null} */
        let result = null;
        // Pair the units belong to while the requests run.
        let reachedPairId = currentPairId;

        try {
          const cart = await (await fetch('/cart.js')).json();
          // PP-1019: the options were rendered for the customer type of that moment;
          // the cart decides now. A private cart gets no warranty, the page follows.
          if (cartIsPrivateCustomer(cart)) {
            render(null);
            return;
          }
          const moved = await moveWarrantyUnits(cart, {
            parentVariantId,
            addedProperties,
            quantity: addedQuantity ?? 1,
            fromPairId: currentPairId,
            warranty: removes
              ? null
              : { variantId: pickedVariantId, sku: toggle.dataset.variantSku ?? '' },
            onMoved: (pairId) => {
              reachedPairId = pairId;
            },
          });
          currentPairId = moved.pairId;
          result = moved;
        } catch (error) {
          console.error('Warranty selection in the add-to-cart popup failed:', error);
          await repairWarrantyPairs();
          // Show what the cart really contains now instead of guessing. Without
          // section markup the cart surfaces render themselves freshly.
          const actual = await readWarrantyState(reachedPairId);
          currentPairId = actual.pairId;
          render(actual.warrantyVariantId);
          // No message when only the clean-up failed and the repair made up for it.
          const wantedVariantId = removes ? null : pickedVariantId;
          if (errorMessage instanceof HTMLElement) {
            errorMessage.hidden = actual.warrantyVariantId === wantedVariantId;
          }
          if (actual.cart) {
            document.dispatchEvent(
              new CartUpdateEvent(actual.cart, 'warranty-addons', {
                source: 'warranty-addons',
                itemCount: actual.cart.item_count,
              })
            );
          }
        } finally {
          option?.classList.remove('is-pending');
          setBusy(false);
        }

        if (result) await refreshCartSurfaces(result.itemCount);
      });
    });
  });
}

/**
 * Reads what the cart holds for a pair token: the token only counts when both the
 * product line and its warranty line are in the cart.
 * @param {string | null} pairId
 * @returns {Promise<{ pairId: string | null, warrantyVariantId: string | null, cart: any }>}
 */
async function readWarrantyState(pairId) {
  const cart = await readCart();
  if (!cart) return { pairId: null, warrantyVariantId: null, cart: null };
  const parent = pairId
    ? cart.items.find(
        (/** @type {any} */ item) => !isWarrantyLine(item) && pairRefOf(item) === pairId
      )
    : null;
  const warranty = parent
    ? cart.items.find(
        (/** @type {any} */ item) => isWarrantyLine(item) && pairRefOf(item) === pairId
      )
    : null;
  return {
    pairId: warranty ? pairId : null,
    warrantyVariantId: warranty ? String(warranty.variant_id) : null,
    cart,
  };
}

initWarrantyAddons();
document.addEventListener('shopify:section:load', initWarrantyAddons);
// PP-906: drawers injected for list add-to-carts (product-form.js) arrive after this
// module already ran — re-bind on demand. Binding is idempotent per drawer.
document.addEventListener('warranty:drawer-injected', initWarrantyAddons);
